import { getHelper } from '@/devtools/utils';
import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import { usePfeInputStore } from '@/features/Sparten/pfe/useInputStore';
import type { SpartenConfig } from '@/features/Sparten/types';
import { SORTABLE_ATTRIBUTES } from '@/features/Tariffs/sorting';
import { formatCurrency, formatMillions } from '@/utils/formatters/euro';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { formatYears } from '@/utils/formatters/years';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import {
  KEEP_DATA_FROM_QUOTE_MAPPING,
  keepDataFromQuoteMappings,
} from '@/features/Sparten/shared/keepDataFromQuoteMappings';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { pfeVersicherungsangabenConfig } from '@/features/Sparten/pfe/versicherungsangabenConfig';
import image from './assets/bg.jpg';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/pfe',
  props: {
    name: 'Pferdehalterhaftpflichtversicherung',
    sparte: 'pfe',
    image,
  },
  general: {
    distribution: {
      channel: true,
      pooling: true,
    },

    maxSimultaneousCount: 3,

    hasRisikoDEMVerCertificate: true,

    versicherungsangabenConfig: pfeVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum'],
      },
    },

    profileFields: ['deckungssumme', 'selbstbeteiligung'],

    keepDataFromQuote: {
      clients: {
        versicherungsnehmer: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
      },
    },

    buildInputDataObject: () =>
      useStorableState().create(usePfeInputStore().$state),

    buildContractDataObject: (state) => state,

    getSortableAttributes: () => [
      SORTABLE_ATTRIBUTES.DECKUNGSSUMME,
      SORTABLE_ATTRIBUTES.SELBSTBETEILIGUNG,
      SORTABLE_ATTRIBUTES.LAUFZEIT,
    ],

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Laufzeit',
          fieldPath: 'laufzeit',
          formatter: formatYears,
        },
        {
          label: 'Deckungssumme',
          fieldPath: 'deckungssumme',
          inputFormatter: formatChoice(SIMPLE_CHOICE.DECKUNGSSUMME),
          formatter: formatMillions,
        },
        {
          label: 'Selbstbeteiligung',
          fieldPath: 'selbstbeteiligung',
          formatter: formatCurrency,
          inputFormatter: formatChoice(SIMPLE_CHOICE.SELBSTBETEILIGUNG),
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
      ],
    },

    baseTariffComponent: () =>
      import('@/features/Tariffs/BaseTariff/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/pfe/components/TariffDetails/TariffDetails.vue'
      ),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('pfe').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('pfe').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('pfe').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('pfe').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
    blockCompanyClient: true,
  },

  steps: {
    input: {
      component: () => import('@/features/Sparten/pfe/views/InputView.vue'),
    },
    offers: {
      component: () => import('@/features/Sparten/pfe/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/pfe/views/Comparison.vue'),
    },

    contract: {
      component: () => import('@/features/Sparten/pfe/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};
